import { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';



import { Box, Typography } from '@mui/material';
import { InfoBar } from '@rent/ui';
import { validatePolish } from '@rent/ui/helpers';
import { FormFieldGenerator, SelectAll, TextLink } from 'components/shared';
import { ExternalLinks, OrderStatus } from 'consts';
import { OrderContext } from 'context';
import { useFormFieldErrorFocus } from 'hooks'
import { CustomerMaskedData, CustomerPersonalDataConsentsFields, CustomerPersonalDataFields, IMappedProduct } from 'models'


import { ValidationSchema, customerPersonalDataConsentsFieldsProperties, customerPersonalDataFieldsProperties } from '../consts';
import { MarketingConsents } from '../hooks';


interface FormDataProps {
  onSubmitForm: SubmitHandler<ValidationSchema>
  orderStatus: OrderStatus | ''
  extension: boolean
  setProvidedNip: (nip: string) => void
  marketingConsents: MarketingConsents
  isPlugin?: boolean
  customerMaskedData?: CustomerMaskedData | null
  orderProductsData?: IMappedProduct[] | null
}

const FormData = ({
  onSubmitForm,
  orderStatus,
  extension,
  setProvidedNip,
  marketingConsents,
  isPlugin,
  customerMaskedData,
  orderProductsData,
}: FormDataProps) => {
  const { formatMessage } = useIntl()
  const { contextData } = useContext(OrderContext)

  useFormFieldErrorFocus()
  const { control, setValue, setFocus } = useFormContext()

  const watchedNip = useWatch({ control, name: CustomerPersonalDataFields.nip })
  const editableFields = useWatch({ control, name: 'editableFields' })
  const [initialNip, setInitialNip] = useState(false)
  useEffect(() => {
    if (validatePolish.nip(watchedNip)) {
      setProvidedNip(watchedNip)
      if (contextData.customerOrderType === 'API') {
        setInitialNip(true)
      }
    } else {
      setProvidedNip('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedNip])

  useEffect(() => {
    setFocus(CustomerPersonalDataFields.firstName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // automatic selection of marketing consents based on provided nip
    if (marketingConsents.electronicProcessingAccepted) {
      setValue(CustomerPersonalDataConsentsFields.agreementEmailCommunication, marketingConsents.electronicProcessingAccepted)
    }
    if (marketingConsents.phoneProcessingAccepted) {
      setValue(CustomerPersonalDataConsentsFields.agreementPhoneCommunication, marketingConsents.phoneProcessingAccepted)
    }
    if (marketingConsents.loanElectronicProcessingAccepted) {
      setValue(CustomerPersonalDataConsentsFields.loanAgreementEmailCommunication, marketingConsents.loanElectronicProcessingAccepted)
    }
    if (marketingConsents.loanPhoneProcessingAccepted) {
      setValue(CustomerPersonalDataConsentsFields.loanAgreementPhoneCommunication, marketingConsents.loanPhoneProcessingAccepted)
    }
    if (marketingConsents.loanElectronicProcessingAccepted) {
      setValue(CustomerPersonalDataConsentsFields.loan2AgreementEmailCommunication, marketingConsents.loan2ElectronicProcessingAccepted)
    }
    if (marketingConsents.loanPhoneProcessingAccepted) {
      setValue(CustomerPersonalDataConsentsFields.loan2AgreementPhoneCommunication, marketingConsents.loan2PhoneProcessingAccepted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketingConsents])

  const onEditClick = (fieldName: string) => {
    setValue(`editableFields.${fieldName}`, !editableFields[fieldName])
    setTimeout(() => {
      setFocus(fieldName)
    }, 50)
  }

  return (
    <Box sx={{ maxWidth: 500 }}>
      <>
        <Box mb={{ xs: 3.2, md: 6 }}>
          <Typography variant="h4" sx={{ mb: { xs: 2.4, md: 3.2 } }}>
            {formatMessage({ id: contextData.isLoan ? 'customer.buyer_data_loan' : 'customer.buyer_data' })}
          </Typography>
          {Object.entries(customerPersonalDataFieldsProperties(orderStatus, initialNip)).map(([key, el]) => (
            <Box key={key} mb={{ xs: 2.4, md: 3.2 }}>
              <FormFieldGenerator
                fieldName={key as CustomerPersonalDataFields}
                properties={{
                  ...el,
                  ...(customerMaskedData && {
                    editableInitValue: customerMaskedData[key as keyof CustomerMaskedData] ?? '',
                    isNotEditable: editableFields[key],
                    onEditClick,
                  }),
                }}
                onSubmitForm={onSubmitForm}
              />
            </Box>
          ))}
        </Box>

        {/* address */}
        {!extension && !contextData.isLoan && (
          <Box mb={{ xs: 3.2, md: 6 }}>
            <Typography variant="h4" sx={{ mb: 2.4 }}>
              {formatMessage({ id: 'customer.delivery_method' })}
            </Typography>
            <InfoBar
              title={formatMessage({
                id: isPlugin ? 'message.address_for_plugin_transaction' : 'message.address_was_provided_in_the_store',
              })}
              align="left"
            />
          </Box>
        )}

        {/* consents */}
        <Box>
          <SelectAll
            fields={CustomerPersonalDataConsentsFields}
            disabledFields={{
              [CustomerPersonalDataConsentsFields.agreementEmailCommunication]: marketingConsents.electronicProcessingAccepted,
              [CustomerPersonalDataConsentsFields.agreementPhoneCommunication]: marketingConsents.phoneProcessingAccepted,
              [CustomerPersonalDataConsentsFields.loanAgreementEmailCommunication]: marketingConsents.loanElectronicProcessingAccepted,
              [CustomerPersonalDataConsentsFields.loanAgreementPhoneCommunication]: marketingConsents.loanPhoneProcessingAccepted,
              [CustomerPersonalDataConsentsFields.loan2AgreementEmailCommunication]: marketingConsents.loan2ElectronicProcessingAccepted,
              [CustomerPersonalDataConsentsFields.loan2AgreementPhoneCommunication]: marketingConsents.loan2PhoneProcessingAccepted,
            }}
          />

          <Box ml={2.4} mt={1.6}>
            {Object.entries(customerPersonalDataConsentsFieldsProperties(marketingConsents, formatMessage, contextData.isLoan, contextData.preQualificationForFreeInstallment && !(orderProductsData ?? []).some(p => p.flexAdditionalServiceTaken))).map(
              ([key, el]) => (
                <Box key={key} mb={1.6}>
                  <FormFieldGenerator fieldName={key as CustomerPersonalDataFields} properties={el} />
                </Box>
              ),
            )}
            {(marketingConsents.electronicProcessingAccepted || marketingConsents.phoneProcessingAccepted) && (
              <InfoBar
                title={formatMessage(
                  { id: 'message.marketingContentsAlreadyAccepted' },
                  {
                    link: (
                      <TextLink href={ExternalLinks.WEBOOK} id="rodo-link">
                        WeBook
                      </TextLink>
                    ),
                  },
                )}
                align="left"
                sx={{ mt: -0.6 }}
              />
            )}
          </Box>
        </Box>
      </>
    </Box>
  )
}

export default FormData
